import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Heading } from '@library';
import ProductList from '../ProductList/List/ProductList';
import useCategory from '../../../core/queries/useCategory';
import FeaturedFilter from '../ProductList/Filters/FeaturedFilter';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import assert from '../../../core/util/assert';
import Container from '../../../core/components/Container';

const useSearchParams = (search: string) => {
    const searchParams = new URLSearchParams(search);
    const searchText = searchParams.get('q');
    return {
        searchText,
        searchParams,
    };
};

const KeywordSearchResults = memo(() => {
    const intl = useIntl();
    const { search } = useLocation();
    const { searchText, searchParams } = useSearchParams(search);
    const { categoryId, categoryName } = useCategory();

    assert(searchText, 'Empty Search');

    return (
        <ProductList
            ignoreList={['q']}
            featuredFilter={
                <FeaturedFilter
                    name={intl.formatMessage({
                        id: 'search term',
                    })}
                    value={searchText}
                />
            }
            filters={{
                includeExcludedCategories: false,
            }}
            header={
                <Heading h3 bold>
                    <FormattedMessage
                        id={'your search for "{searchTerm}"'}
                        values={{
                            searchTerm: searchText,
                        }}
                    />
                    {categoryId ? (
                        ` - ${categoryName}`
                    ) : (
                        <>
                            {' '}
                            -{' '}
                            {intl.formatMessage({
                                id: 'all categories',
                            })}
                        </>
                    )}
                </Heading>
            }
            rootLinks={[
                {
                    text: `${intl.formatMessage({ id: 'search_noun' })}:${searchText}`,
                    to: `/search?${searchParams.toString()}`,
                },
            ]}
            isSearchResultPage
        />
    );
});

export default memo(
    withErrorBoundary(KeywordSearchResults, {
        errorComponent: ({ defaultErrorMessage }) => (
            <Container>
                <br />
                {defaultErrorMessage}
            </Container>
        ),
    })
);
